import React, {
    useCallback,
    useEffect,
    useMemo,
} from "react";

import {
    FiAlertCircle,
    FiArrowLeft,
    FiClock,
    FiCreditCard,
    FiDollarSign,
    FiFileText,
    FiRefreshCw,
    FiUser,
} from "react-icons/fi";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import {
    useHistory,
    useParams,
} from "react-router-dom";

import {
    clearSelectedSupplierPayable,
    clearSupplierPayableBills,
    clearSupplierPayableLedgerBalance,

    fetchSupplierPayableDetails,
    fetchSupplierPayableBills,
    fetchSupplierPayableLedgerBalance,

    selectedSupplierPayableSelector,
    supplierPayableBillsSelector,
    supplierPayableBillsSummarySelector,
    supplierPayableLedgerBalanceSelector,
    supplierPayableLoadingSelector,
    supplierPayableErrorSelector,
} from "../../../../../../store/billing/supplierPayable/supplierPayableSlice";

import "./SupplierPayables.css";


/*
|--------------------------------------------------------------------------
| HELPERS
|--------------------------------------------------------------------------
*/

const toNumber = (
    value,
    fallback = 0
) => {
    const number =
        Number(value);

    return Number.isFinite(
        number
    )
        ? number
        : fallback;
};


const roundMoney = (
    value
) =>
    Math.round(
        (
            toNumber(
                value
            ) +
            Number.EPSILON
        ) *
            100
    ) / 100;


const formatCurrency = (
    value
) =>
    new Intl.NumberFormat(
        "en-IN",
        {
            style:
                "currency",

            currency:
                "INR",

            minimumFractionDigits:
                2,

            maximumFractionDigits:
                2,
        }
    ).format(
        roundMoney(
            value
        )
    );


const formatDate = (
    value
) => {
    if (
        !value
    ) {
        return "-";
    }

    const date =
        new Date(
            value
        );

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "-";
    }

    return new Intl.DateTimeFormat(
        "en-IN",
        {
            day:
                "2-digit",

            month:
                "short",

            year:
                "numeric",
        }
    ).format(
        date
    );
};


const getSupplierName = (
    supplier
) =>
    supplier
        ?.supplierName ||
    supplier
        ?.companyName ||
    "Supplier";


const getSupplierMobile = (
    supplier
) =>
    supplier?.mobile ||
    "-";


const getSupplierEmail = (
    supplier
) =>
    supplier?.email ||
    "-";


const getSupplierGstin = (
    supplier
) =>
    supplier?.gstin ||
    "-";


const getSupplierCode = (
    supplier
) =>
    supplier
        ?.supplierCode ||
    "-";


const getAgeingClass = (
    bucket
) => {
    switch (
        bucket
    ) {
        case "0_30":
            return "supplier-payable-ageing-0-30";

        case "31_60":
            return "supplier-payable-ageing-31-60";

        case "61_90":
            return "supplier-payable-ageing-61-90";

        case "90_PLUS":
            return "supplier-payable-ageing-90-plus";

        default:
            return "supplier-payable-ageing-not-due";
    }
};


const getAgeingLabel = (
    bucket
) => {
    switch (
        bucket
    ) {
        case "0_30":
            return "0 - 30 Days";

        case "31_60":
            return "31 - 60 Days";

        case "61_90":
            return "61 - 90 Days";

        case "90_PLUS":
            return "90+ Days";

        default:
            return "Not Due";
    }
};


/*
|--------------------------------------------------------------------------
| COMPONENT
|--------------------------------------------------------------------------
*/

const SupplierPayableDetailsPage =
    () => {
        const dispatch =
            useDispatch();

        const history =
            useHistory();

        const {
            supplierId,
        } =
            useParams();


        /*
        |--------------------------------------------------------------------------
        | REDUX
        |--------------------------------------------------------------------------
        */

        const details =
            useSelector(
                selectedSupplierPayableSelector
            ) || {};

        const bills =
            useSelector(
                supplierPayableBillsSelector
            ) || [];

        const billsSummary =
            useSelector(
                supplierPayableBillsSummarySelector
            ) || {};

        const ledgerBalance =
            useSelector(
                supplierPayableLedgerBalanceSelector
            ) || {};

        const loading =
            useSelector(
                supplierPayableLoadingSelector
            ) || {};

        const errors =
            useSelector(
                supplierPayableErrorSelector
            ) || {};


        /*
        |--------------------------------------------------------------------------
        | DATA
        |--------------------------------------------------------------------------
        */

        const supplier =
            details
                ?.supplier ||
            null;

        const totals =
            details
                ?.totals ||
            {};

        const ageing =
            details
                ?.ageing ||
            {};


        /*
        |--------------------------------------------------------------------------
        | LOAD DATA
        |--------------------------------------------------------------------------
        */

        const loadData =
            useCallback(
                async () => {
                    if (
                        !supplierId
                    ) {
                        return;
                    }

                    await Promise.all([
                        dispatch(
                            fetchSupplierPayableDetails({
                                supplierId,
                            })
                        ),

                        dispatch(
                            fetchSupplierPayableBills({
                                supplierId,
                            })
                        ),

                        dispatch(
                            fetchSupplierPayableLedgerBalance(
                                supplierId
                            )
                        ),
                    ]);
                },
                [
                    dispatch,
                    supplierId,
                ]
            );


        useEffect(
            () => {
                loadData();

                return () => {
                    dispatch(
                        clearSelectedSupplierPayable()
                    );

                    dispatch(
                        clearSupplierPayableBills()
                    );

                    dispatch(
                        clearSupplierPayableLedgerBalance()
                    );
                };
            },
            [
                loadData,
                dispatch,
            ]
        );


        /*
        |--------------------------------------------------------------------------
        | AGEING CARDS
        |--------------------------------------------------------------------------
        */

        const ageingCards =
            useMemo(
                () => [
                    {
                        label:
                            "Not Due",

                        value:
                            ageing?.notDue,
                    },

                    {
                        label:
                            "0 - 30 Days",

                        value:
                            ageing?.days0To30,
                    },

                    {
                        label:
                            "31 - 60 Days",

                        value:
                            ageing?.days31To60,
                    },

                    {
                        label:
                            "61 - 90 Days",

                        value:
                            ageing?.days61To90,
                    },

                    {
                        label:
                            "90+ Days",

                        value:
                            ageing?.days90Plus,
                    },
                ],
                [
                    ageing,
                ]
            );


        /*
        |--------------------------------------------------------------------------
        | LOADING
        |--------------------------------------------------------------------------
        */

        const isInitialLoading =
            loading.details &&
            !supplier;


        /*
        |--------------------------------------------------------------------------
        | RENDER
        |--------------------------------------------------------------------------
        */

        return (
            <div className="supplier-payable-page">

                {/* =====================================================
                    HEADER
                ===================================================== */}

                <header className="supplier-payable-header">

                    <div className="supplier-payable-header-left">

                        <button
                            type="button"
                            className="supplier-payable-icon-button"
                            onClick={() =>
                                history.push(
                                    "/billing/supplier-payables"
                                )
                            }
                        >
                            <FiArrowLeft />
                        </button>


                        <div>
                            <small>
                                SUPPLIER PAYABLE
                            </small>

                            <h1>
                                {getSupplierName(
                                    supplier
                                )}
                            </h1>

                            <p>
                                Outstanding bills, ageing and ledger reconciliation.
                            </p>
                        </div>

                    </div>


                    <button
                        type="button"
                        className="supplier-payable-button"
                        disabled={
                            loading.details ||
                            loading.bills ||
                            loading.ledgerBalance
                        }
                        onClick={
                            loadData
                        }
                    >
                        <FiRefreshCw
                            className={
                                loading.details ||
                                loading.bills ||
                                loading.ledgerBalance
                                    ? "supplier-payable-spinner"
                                    : ""
                            }
                        />

                        Refresh
                    </button>

                </header>


                {/* =====================================================
                    ERROR
                ===================================================== */}

                {(errors.details ||
                    errors.bills ||
                    errors.ledgerBalance) && (
                    <div className="supplier-payable-alert">
                        <FiAlertCircle />

                        <span>
                            {errors.details ||
                                errors.bills ||
                                errors.ledgerBalance}
                        </span>
                    </div>
                )}


                {/* =====================================================
                    LOADING
                ===================================================== */}

                {isInitialLoading ? (
                    <div className="supplier-payable-empty">
                        <FiRefreshCw className="supplier-payable-spinner" />

                        <strong>
                            Loading supplier payable details...
                        </strong>
                    </div>
                ) : (
                    <>

                        {/* =================================================
                            SUPPLIER INFO
                        ================================================= */}

                        <section className="supplier-payable-detail-card">

                            <div className="supplier-payable-detail-title">
                                <FiUser />

                                <div>
                                    <h3>
                                        Supplier Information
                                    </h3>

                                    <p>
                                        Supplier master details.
                                    </p>
                                </div>
                            </div>


                            <div className="supplier-payable-detail-grid">

                                <div>
                                    <span>
                                        Supplier Code
                                    </span>

                                    <strong>
                                        {getSupplierCode(
                                            supplier
                                        )}
                                    </strong>
                                </div>


                                <div>
                                    <span>
                                        Supplier Name
                                    </span>

                                    <strong>
                                        {getSupplierName(
                                            supplier
                                        )}
                                    </strong>
                                </div>


                                <div>
                                    <span>
                                        Mobile
                                    </span>

                                    <strong>
                                        {getSupplierMobile(
                                            supplier
                                        )}
                                    </strong>
                                </div>


                                <div>
                                    <span>
                                        Email
                                    </span>

                                    <strong>
                                        {getSupplierEmail(
                                            supplier
                                        )}
                                    </strong>
                                </div>


                                <div>
                                    <span>
                                        GSTIN
                                    </span>

                                    <strong>
                                        {getSupplierGstin(
                                            supplier
                                        )}
                                    </strong>
                                </div>


                                <div>
                                    <span>
                                        PAN
                                    </span>

                                    <strong>
                                        {supplier
                                            ?.pan ||
                                            "-"}
                                    </strong>
                                </div>

                            </div>

                        </section>


                        {/* =================================================
                            SUMMARY CARDS
                        ================================================= */}

                        <section className="supplier-payable-summary-grid">

                            <div className="supplier-payable-summary-card supplier-payable-summary-primary">

                                <div className="supplier-payable-summary-top">
                                    <span className="supplier-payable-summary-icon">
                                        <FiDollarSign />
                                    </span>

                                    <span>
                                        Outstanding
                                    </span>
                                </div>

                                <strong>
                                    {formatCurrency(
                                        totals
                                            ?.totalOutstanding
                                    )}
                                </strong>

                                <small>
                                    Current payable balance
                                </small>
                            </div>


                            <div className="supplier-payable-summary-card supplier-payable-summary-danger">

                                <div className="supplier-payable-summary-top">
                                    <span className="supplier-payable-summary-icon">
                                        <FiClock />
                                    </span>

                                    <span>
                                        Overdue
                                    </span>
                                </div>

                                <strong>
                                    {formatCurrency(
                                        ageing
                                            ?.overdueAmount
                                    )}
                                </strong>

                                <small>
                                    {toNumber(
                                        totals
                                            ?.overdueBills
                                    )} overdue bills
                                </small>
                            </div>


                            <div className="supplier-payable-summary-card supplier-payable-summary-success">

                                <div className="supplier-payable-summary-top">
                                    <span className="supplier-payable-summary-icon">
                                        <FiCreditCard />
                                    </span>

                                    <span>
                                        Paid
                                    </span>
                                </div>

                                <strong>
                                    {formatCurrency(
                                        totals
                                            ?.totalPaid
                                    )}
                                </strong>

                                <small>
                                    Supplier payments
                                </small>
                            </div>


                            <div className="supplier-payable-summary-card supplier-payable-summary-warning">

                                <div className="supplier-payable-summary-top">
                                    <span className="supplier-payable-summary-icon">
                                        <FiFileText />
                                    </span>

                                    <span>
                                        Debit Notes
                                    </span>
                                </div>

                                <strong>
                                    {formatCurrency(
                                        totals
                                            ?.totalDebitNotes
                                    )}
                                </strong>

                                <small>
                                    Payable adjustments
                                </small>
                            </div>

                        </section>


                        {/* =================================================
                            LEDGER RECONCILIATION
                        ================================================= */}

                        <section className="supplier-payable-reconciliation">

                            <div>
                                <span>
                                    Bill Outstanding
                                </span>

                                <strong>
                                    {formatCurrency(
                                        totals
                                            ?.totalOutstanding
                                    )}
                                </strong>
                            </div>


                            <div>
                                <span>
                                    Supplier Ledger Balance
                                </span>

                                <strong>
                                    {formatCurrency(
                                        ledgerBalance
                                            ?.balance
                                    )}
                                </strong>
                            </div>


                            <div>
                                <span>
                                    Difference
                                </span>

                                <strong
                                    className={
                                        Math.abs(
                                            roundMoney(
                                                toNumber(
                                                    totals
                                                        ?.totalOutstanding
                                                ) -
                                                toNumber(
                                                    ledgerBalance
                                                        ?.balance
                                                )
                                            )
                                        ) >
                                        0.01
                                            ? "supplier-payable-reconciliation-error"
                                            : "supplier-payable-reconciliation-success"
                                    }
                                >
                                    {formatCurrency(
                                        roundMoney(
                                            toNumber(
                                                totals
                                                    ?.totalOutstanding
                                            ) -
                                            toNumber(
                                                ledgerBalance
                                                    ?.balance
                                            )
                                        )
                                    )}
                                </strong>
                            </div>

                        </section>


                        {/* =================================================
                            AGEING
                        ================================================= */}

                        <section className="supplier-payable-detail-card">

                            <div className="supplier-payable-detail-title">
                                <FiClock />

                                <div>
                                    <h3>
                                        Ageing Summary
                                    </h3>

                                    <p>
                                        Outstanding amount grouped by due age.
                                    </p>
                                </div>
                            </div>


                            <div className="supplier-payable-ageing-grid">

                                {ageingCards.map(
                                    (
                                        item
                                    ) => (
                                        <div
                                            key={
                                                item.label
                                            }
                                        >
                                            <span>
                                                {item.label}
                                            </span>

                                            <strong>
                                                {formatCurrency(
                                                    item.value
                                                )}
                                            </strong>
                                        </div>
                                    )
                                )}

                            </div>

                        </section>


                        {/* =================================================
                            OUTSTANDING BILLS
                        ================================================= */}

                        <section className="supplier-payable-detail-card">

                            <div className="supplier-payable-detail-title">
                                <FiFileText />

                                <div>
                                    <h3>
                                        Outstanding Purchase Bills
                                    </h3>

                                    <p>
                                        Confirmed purchase bills with a remaining payable balance.
                                    </p>
                                </div>
                            </div>


                            {loading.bills &&
                            bills.length ===
                                0 ? (
                                <div className="supplier-payable-empty">

                                    <FiRefreshCw className="supplier-payable-spinner" />

                                    <strong>
                                        Loading outstanding bills...
                                    </strong>

                                </div>
                            ) : bills.length ===
                                0 ? (
                                <div className="supplier-payable-empty">

                                    <FiFileText />

                                    <strong>
                                        No outstanding bills
                                    </strong>

                                    <span>
                                        This supplier currently has no payable purchase bills.
                                    </span>

                                </div>
                            ) : (
                                <div className="supplier-payable-table-wrapper">

                                    <table className="supplier-payable-table">

                                        <thead>
                                            <tr>
                                                <th>
                                                    Purchase Bill
                                                </th>

                                                <th>
                                                    Bill Date
                                                </th>

                                                <th>
                                                    Due Date
                                                </th>

                                                <th className="text-right">
                                                    Bill Amount
                                                </th>

                                                <th className="text-right">
                                                    Debit Notes
                                                </th>

                                                <th className="text-right">
                                                    Paid
                                                </th>

                                                <th className="text-right">
                                                    Outstanding
                                                </th>

                                                <th>
                                                    Ageing
                                                </th>
                                            </tr>
                                        </thead>


                                        <tbody>

                                            {bills.map(
                                                (
                                                    bill,
                                                    index
                                                ) => (
                                                    <tr
                                                        key={
                                                            bill
                                                                ?._id ||
                                                            bill
                                                                ?.purchaseBillId ||
                                                            index
                                                        }
                                                    >

                                                        <td>
                                                            <strong className="supplier-payable-bill-number">
                                                                {bill
                                                                    ?.documentNumber ||
                                                                    bill
                                                                        ?.purchaseBillNumber ||
                                                                    "-"}
                                                            </strong>
                                                        </td>


                                                        <td>
                                                            {formatDate(
                                                                bill
                                                                    ?.documentDate
                                                            )}
                                                        </td>


                                                        <td>
                                                            {formatDate(
                                                                bill
                                                                    ?.dueDate
                                                            )}
                                                        </td>


                                                        <td className="text-right">
                                                            {formatCurrency(
                                                                bill
                                                                    ?.grandTotal
                                                            )}
                                                        </td>


                                                        <td className="text-right">
                                                            {formatCurrency(
                                                                bill
                                                                    ?.debitNoteAmount
                                                            )}
                                                        </td>


                                                        <td className="text-right">
                                                            {formatCurrency(
                                                                bill
                                                                    ?.paidAmount
                                                            )}
                                                        </td>


                                                        <td className="text-right">
                                                            <strong className="supplier-payable-outstanding">
                                                                {formatCurrency(
                                                                    bill
                                                                        ?.outstandingAmount
                                                                )}
                                                            </strong>
                                                        </td>


                                                        <td>
                                                            <span
                                                                className={`supplier-payable-ageing-badge ${getAgeingClass(
                                                                    bill
                                                                        ?.ageingBucket
                                                                )}`}
                                                            >
                                                                {getAgeingLabel(
                                                                    bill
                                                                        ?.ageingBucket
                                                                )}

                                                                {toNumber(
                                                                    bill
                                                                        ?.daysOverdue
                                                                ) >
                                                                    0 && (
                                                                    <>
                                                                        {" • "}
                                                                        {
                                                                            bill
                                                                                .daysOverdue
                                                                        }d
                                                                    </>
                                                                )}
                                                            </span>
                                                        </td>

                                                    </tr>
                                                )
                                            )}

                                        </tbody>

                                    </table>

                                </div>
                            )}


                            {/* BILLS SUMMARY */}

                            <div className="supplier-payable-bills-footer">

                                <div>
                                    <span>
                                        Bills
                                    </span>

                                    <strong>
                                        {toNumber(
                                            billsSummary
                                                ?.totalBills
                                        )}
                                    </strong>
                                </div>


                                <div>
                                    <span>
                                        Overdue Bills
                                    </span>

                                    <strong>
                                        {toNumber(
                                            billsSummary
                                                ?.overdueBills
                                        )}
                                    </strong>
                                </div>


                                <div>
                                    <span>
                                        Outstanding
                                    </span>

                                    <strong>
                                        {formatCurrency(
                                            billsSummary
                                                ?.totalOutstanding
                                        )}
                                    </strong>
                                </div>


                                <div>
                                    <span>
                                        Overdue
                                    </span>

                                    <strong>
                                        {formatCurrency(
                                            billsSummary
                                                ?.overdueAmount
                                        )}
                                    </strong>
                                </div>

                            </div>

                        </section>

                    </>
                )}

            </div>
        );
    };


export default SupplierPayableDetailsPage;